/*
 * @Description: 移动端创作弹窗状态管理
 * @Date: 2026-01-12
 */
import { defineStore } from 'pinia'

/**
 * 工具类型枚举
 */
export type ToolType =
    | 'text-to-image'
    | 'text-to-video'
    | 'image-to-video'
    | 'text-to-music'
    | 'image-to-image'
    | 'ai-tools'
    | 'photo-effects'
    | 'video-effects'
    | 'video-to-video'

/**
 * 工具路径映射
 */
export const TOOL_PATH_MAP: Record<ToolType, string> = {
    'text-to-image': '/text-to-image',
    'text-to-video': '/text-to-video',
    'image-to-video': '/image-to-video',
    'text-to-music': '/text-to-music',
    'image-to-image': '/image-to-image',
    'ai-tools': '/ai-tools',
    'photo-effects': '/photo-effects',
    'video-effects': '/video-effects',
    'video-to-video': '/video-to-video',
}

/**
 * 路径到工具类型的映射
 */
export const PATH_TO_TOOL_MAP: Record<string, ToolType> = Object.entries(
    TOOL_PATH_MAP
).reduce(
    (acc, [tool, path]) => {
        acc[path] = tool as ToolType
        return acc
    },
    {} as Record<string, ToolType>
)

/**
 * 创作弹窗状态接口
 */
interface CreationModalState {
    /** 弹窗是否显示 */
    visible: boolean
    /** 当前选中的工具类型 */
    currentTool: ToolType
    /** 弹窗打开时携带的初始数据 */
    initialData: Record<string, any> | null
}

/**
 * 创作弹窗状态管理 Store
 */
export const useCreationModalStore = defineStore('creationModal', {
    state: (): CreationModalState => ({
        visible: false,
        currentTool: 'text-to-image',
        initialData: null,
    }),

    getters: {
        /** 获取当前工具对应的路径 */
        currentToolPath(): string {
            return TOOL_PATH_MAP[this.currentTool] || '/text-to-image'
        },
    },

    actions: {
        /**
         * 打开创作弹窗
         * @param tool 工具类型
         * @param data 初始数据（可选）
         */
        open(tool: ToolType, data?: Record<string, any>) {
            this.currentTool = tool
            this.initialData = data || null
            this.visible = true
        },

        /**
         * 关闭创作弹窗
         */
        close() {
            this.visible = false
            // 关闭时清空初始数据
            this.initialData = null
        },

        /**
         * 切换工具类型
         * @param tool 工具类型
         */
        switchTool(tool: ToolType) {
            this.currentTool = tool
        },

        /**
         * 根据路径设置当前工具
         * @param path 路径
         */
        setToolByPath(path: string) {
            // 移除语言前缀
            const normalizedPath = path.replace(/^\/[a-z]{2}\//, '/')
            const tool = PATH_TO_TOOL_MAP[normalizedPath]
            if (tool) {
                this.currentTool = tool
            }
        },

        /**
         * 切换弹窗显示状态
         */
        toggle() {
            this.visible = !this.visible
        },

        /**
         * 保存弹窗状态到 sessionStorage（跳转创作页面前调用）
         */
        savePendingState() {
            if (typeof window !== 'undefined') {
                sessionStorage.setItem('creationModal_pending', JSON.stringify({
                    visible: true,
                    currentTool: this.currentTool,
                    initialData: this.initialData,
                }))
            }
        },

        /**
         * 恢复弹窗状态（从创作页面返回时调用）
         */
        restorePendingState() {
            if (typeof window !== 'undefined') {
                const pending = sessionStorage.getItem('creationModal_pending')
                if (pending) {
                    try {
                        const state = JSON.parse(pending)
                        this.currentTool = state.currentTool
                        this.initialData = state.initialData
                        this.visible = true
                        sessionStorage.removeItem('creationModal_pending')
                        return true
                    } catch (e) {
                        sessionStorage.removeItem('creationModal_pending')
                    }
                }
            }
            return false
        },

        /**
         * 清除待恢复状态
         */
        clearPendingState() {
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem('creationModal_pending')
            }
        },
    },
})
